<template>
  <section>
    <div class="content-wrapper">
      <errors v-if="isVisibleErrorsModal"></errors>
      <question v-if="display"></question>
      <div class="content-heading">
        All users
        <small data-localize="dashboard.WELCOME"></small>
      </div>

      <breadcrumb class="breadcrumb" v-bind:breadcrumbsData="breadcrumbData" />
      <br />

      <div class="row">
        <spinner v-if="loading"></spinner>
        <div class="col-lg-12">
          <div id="panelChart9" class="panel panel-default">
            <div class="panel-title">Transfer request</div>
            <div class="line-separator"></div>
            <div class="bottom-margin">
              <div class="modal-body mt-lg">
                <form
                  v-if="!transferNote.transactionId"
                  role="form"
                  class="mb-xl top-margin form-row"
                  novalidate
                >
                  <div class="form-group has-feedback searchbox">
                    <input
                      autofocus
                      v-model="search.text"
                      id="searchText"
                      type="text"
                      placeholder="Search account number"
                      autocomplete="off"
                      class="form-control"
                    />
                    <button
                      type="submit"
                      class="btn btn-default btn-lg"
                      @click.prevent="searchAccount()"
                    >
                      <em class="icon-magnifier pr-sm"></em> Search
                    </button>
                  </div>
                </form>
                <div v-if="!search.account && search.hasSearched" class="panel-body">
                  <div
                    class="alert alert-info alert-dismissible mt-lg not-found-alert"
                    role="alert"
                  >
                    <p>
                      <em class="icon-info pr-sm"></em>No results found.
                    </p>
                  </div>
                </div>
                <div v-if="search.account || selectedAccount" class="top-margin">
                  <table class="table table-striped bank-accounts table-fundings">
                    <thead>
                      <tr>
                        <th>Account number</th>
                        <th>Account name</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="selectedAccount">
                        <td>{{selectedAccount.accountId}}</td>
                        <td>{{selectedAccount.accountName}}</td>
                        <td>
                          <span class="action-span green-text">
                            <i class="fa fa-check check"></i>Selected
                          </span>
                        </td>
                      </tr>
                      <tr
                        v-if="search.account && (!selectedAccount || search.account.id!==selectedAccount.id)"
                      >
                        <td>{{search.account.accountId}}</td>
                        <td>{{search.account.accountName}}</td>
                        <td>
                          <button
                            v-if="!selectedAccount || search.account.id!==selectedAccount.id"
                            type="button"
                            class="btn btn-details btn-outline"
                            @click="selectAccount(search.account)"
                          >Select account</button>
                          <span class="action-span green-text" v-else>
                            <i class="fa fa-check check"></i>Selected
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div class="row top-margin">
                <div class="col-md-12">
                  <span>Please append your comment below *</span>
                  <restricted-textarea
                    :input="comment"
                    :limit="255"
                    id="comment"
                    placeholder=""
                    @inputUpdated="commentUpdated"
                    :required="true"
                  />
                  <p
                    class="top-margin"
                    style="color: red;"
                    v-if="!comment || transaction.comment === comment"
                  >
                    <i>You must enter or append a comment.</i>
                  </p>
                </div>
              </div>
              <div class="row top-margin">
                <div class="col-md-10">
                  <button
                    type="submit"
                    class="btn btn-info btn-lg button-white buttons"
                    v-on:click="!transferNote.transactionId ? goToNoteDetail() : showTransferConfirmationModal()"
                    v-bind:disabled="!!transferNote.transactionId && isCancelButtonDisabled()"
                  >Cancel request</button>
                  <button
                    type="submit"
                    class="btn btn-info btn-lg button-white buttons"
                    v-on:click="approveTransfer"
                    v-bind:disabled="!transferNote.transactionId || isButtonDisabled()"
                  >Approve transfer</button>
                  <button
                    type="submit"
                    class="btn btn-info btn-lg button-approve buttons"
                    v-on:click="submitRequest"
                    v-bind:disabled="!!transferNote.transactionId || isButtonDisabled()"
                  >Submit request</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';

import {
  HIDE_QUESTION_MODAL,
  SET_ERRORS_MODAL_INFO,
  SHOW_ERRORS_MODAL,
  SHOW_QUESTION_MODAL,
} from '@/store/mutation-types';

import breadcrumb from '@/components/Breadcrumb';
import Errors from '@/components/modals/Errors';
import Question from '@/components/modals/Question';
import spinner from '@/components/widgets/Spinner';
import { Constants } from '@/data/static/constants';
import { getAccounTypeProperties } from '@/util';
import { UserTypes } from '@/data/static/userTypes';
import { SearchTypes } from '@/data/static/searchTypes';
import RestrictedTextArea from '@/components/formControls/RestrictedTextArea';

export default {
  name: 'transfer',
  beforeMount() {
    this.setParams();
    this.loadTransferNote();
  },
  components: {
    breadcrumb,
    spinner,
    errors: Errors,
    question: Question,
    'restricted-textarea': RestrictedTextArea,
  },
  watch: {
    transferNote: async function changeNote(newTransferNote) {
      const {
        accountName,
        accountType,
        id: accountId,
      } = newTransferNote.fromAccount;
      const users = await this.getAccountUsers(accountId);
      this.breadcrumbData = [];
      this.breadcrumbData.push(
        Object.assign(
          {},
          {
            title: 'Search',
            goto: { name: 'Users' },
            selected: false,
          },
        ),
      );
      const params = Object.assign({}, this.$route.params);
      // prettier-ignore
      const user = users.find(search => search.id === Number(params.userId));
      const { userType, firstName, lastName } = user;
      let goto;
      if (userType === UserTypes.FINANCIAL_ADVISOR.name) {
        goto = {
          name: 'EditFinancialAdvisor',
          params: {
            name: params.name,
            userId: params.userId,
          },
        };
      } else {
        goto = {
          name: 'UserDetail',
          params: { name: params.name, userId: params.userId },
        };
      }
      this.breadcrumbData.push(
        Object.assign(
          {},
          {
            goto,
            title: firstName.concat(' ', lastName),
            selected: false,
          },
        ),
      );
      const accountTypeProperties = getAccounTypeProperties(accountType);
      const gotoAccount = {
        name: accountTypeProperties.component,
        params: {
          accountId,
          name: params.name,
          userId: params.userId,
        },
      };
      this.breadcrumbData.push(
        Object.assign(
          {},
          {
            title: accountName,
            goto: gotoAccount,
            selected: false,
          },
        ),
      );
      this.breadcrumbData.push(
        Object.assign(
          {},
          {
            title: 'Note'.concat(' ', newTransferNote.noteId),
            goto: { name: 'NoteDetail', params: this.$route.params },
            selected: false,
          },
        ),
      );
      this.breadcrumbData.push(
        Object.assign(
          {},
          {
            title: 'Transfer',
            goto: { name: this.$route.name, params: this.$route.params },
            selected: true,
          },
        ),
      );
    },
  },
  data() {
    return {
      noteId: '',
      userId: '',
      name: '',
      note: {},
      transaction: {},
      breadcrumbData: [],
      loading: false,
      transferNote: {},
      comment: '',
      selectedAccount: null,
      search: {
        text: '',
        hasSearched: false,
        account: null,
      },
    };
  },
  computed: {
    ...mapState('errors', ['isVisibleErrorsModal']),
    ...mapState('question', ['display']),
  },
  methods: {
    commentUpdated($event) {
      this.comment = $event;
    },
    ...mapActions('note', [
      'getAccountNoteTransfer',
      'createAccountNoteTransfer',
      'updateAccountNoteTransfer',
      'deleteAccountNoteTransfer',
      'getNote',
    ]),
    ...mapActions('transaction', [
      'updateTransaction',
      'getTransaction',
    ]),
    ...mapActions('account', ['getAccounts', 'getAccountUsers']),
    ...mapMutations('question', {
      hideQuestionModal: HIDE_QUESTION_MODAL,
      showQuestionModal: SHOW_QUESTION_MODAL,
    }),
    ...mapMutations('errors', {
      showErrorsModal: SHOW_ERRORS_MODAL,
      setErrorsModalInfo: SET_ERRORS_MODAL_INFO,
    }),
    setParams() {
      this.name = this.$route.params.name;
      this.userId = this.$route.params.userId;
      this.noteId = this.$route.params.noteId;
    },
    async loadTransferNote() {
      this.loading = true;
      try {
        const note = await this.getNote(this.noteId);
        this.note = note;
        const response = await this.getAccountNoteTransfer({
          noteId: this.noteId,
          accountId: note.accountId,
        });
        this.transferNote = response;
        if (this.transferNote.transactionId) {
          const transaction = await this.getTransaction(this.transferNote.transactionId);
          this.transaction = transaction;
          this.comment = transaction.comment;
          this.selectedAccount = this.transferNote.toAccount;
        }
        this.loading = false;
      } catch (err) {
        this.loading = false;
        if (err !== 'UNAUTHORIZED') {
          this.setErrorsModalInfo([
            'We could not retrieve the transfer note details.',
          ]);
          this.showErrorsModal();
        }
      }
    },
    isCancelButtonDisabled() {
      if (!this.comment) {
        return true;
      }
      if (this.comment === this.transaction.comment) {
        return true;
      }
      return false;
    },
    isButtonDisabled() {
      if (!this.comment) {
        return true;
      }
      if (this.comment === this.transaction.comment) {
        return true;
      }
      if (!this.selectedAccount) {
        return true;
      }
      return false;
    },
    async submitRequest() {
      this.loading = true;
      const transferRequest = {
        toAccountId: this.selectedAccount.id,
        comment: this.comment,
      };
      try {
        await this.createAccountNoteTransfer({
          noteId: this.noteId,
          accountId: this.note.accountId,
          payload: transferRequest,
        });
        this.loading = false;
        this.goToNoteDetail();
      } catch (err) {
        this.loading = false;
        this.setErrorsModalInfo([
          'There was an error when submitting a request.',
        ]);
        this.showErrorsModal();
      }
    },
    async approveTransfer() {
      this.loading = true;
      const transferRequest = {
        comment: this.comment,
      };
      try {
        await this.updateAccountNoteTransfer({
          noteId: this.noteId,
          accountId: this.note.accountId,
          payload: transferRequest,
        });
        this.loading = false;
        this.goToNoteDetail(true);
      } catch (err) {
        this.loading = false;
        this.setErrorsModalInfo([
          'There was an error when approving a request.',
        ]);
        this.showErrorsModal();
      }
    },
    showTransferConfirmationModal() {
      this.showQuestionModal({
        title: Constants.transfer.modalTitle,
        question: Constants.transfer.modalQuestion,
        method: async () => {
          this.loading = true;
          try {
            this.updateTransaction({
              transactionId: this.transferNote.transactionId,
              payload: {
                comment: this.comment,
              },
            });
            await this.deleteAccountNoteTransfer({
              noteId: this.noteId,
              accountId: this.note.accountId,
            });
            this.loading = false;
            this.hideQuestionModal();
            this.goToNoteDetail();
          } catch (err) {
            this.loading = false;
            this.setErrorsModalInfo([
              'There was an error when canceling a request.',
            ]);
            this.showErrorsModal();
            this.hideQuestionModal();
          }
        },
        yesButtonText: Constants.transfer.modalYesButtonText,
        hideCancelButton: false,
      });
    },
    goToNoteDetail(transferApproved) {
      this.$router.push({
        name: 'NoteDetail',
        params: {
          name: this.name,
          noteId: this.noteId,
          userId: transferApproved
            ? this.transferNote.toAccount.primaryInvestorId
            : this.userId,
        },
      });
    },
    isSearchDisabled() {
      return !this.search.text.length > 0;
    },
    async searchAccount() {
      if (this.search.text) {
        try {
          const response = await this.getAccounts({
            searchString: this.search.text,
            searchBy: SearchTypes.ACCOUNT_NUMBER.name,
          });
          this.search.hasSearched = true;
          if (response.statusCode === 401) {
            this.search.account = null;
          } else {
            this.search.account = response.account;
          }
        } catch (err) {
          this.setErrorsModalInfo(['We could not retrieve the account']);
          this.showErrorsModal();
        }
      }
    },
    selectAccount(account) {
      if (account.id === this.transferNote.fromAccount.id) {
        this.setErrorsModalInfo(['Please select a different account']);
        this.showErrorsModal();
      } else {
        this.selectedAccount = account;
      }
    },
  },
};
</script>

<style scoped>
.line-separator {
  padding: 8px;
  line-height: 1.52857143;
  vertical-align: top;
  border-top: 1px solid #eee;
}
.top-margin {
  margin-top: 3%;
}
.bottom-margin {
  margin-bottom: 1%;
}
.space {
  margin-top: 4%;
}
.content-heading {
  font-weight: normal;
}
.panel-title {
  font-size: 20px;
}
.panel-default {
  padding: 30px 40px;
  width: 100%;
}
label {
  color: #888;
  font-size: 14px;
  width: 250px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.button-approve {
  background-color: #127285;
}
.button-white {
  background-color: white;
  border-color: #127285;
  color: #127285;
}
.buttons {
  display: inline-block;
  margin: 5px;
}
.searchbox {
  display: flex;
  flex-direction: row;
}
.searchbox input {
  height: 39px;
  flex: 1;
  border-radius: 4px 0 0 4px;
}
.searchbox .btn-default {
  text-align: center;
  border-radius: 0 4px 4px 0;
}
.green-text {
  color: #558c1d;
  font-weight: 800;
}
.action-span {
  display: block;
  height: 32px;
  min-width: 113px;
  padding: 5px 0;
}
.check {
  margin-right: 5px;
}
.not-found-alert {
  margin-top: -36px !important;
  margin-bottom: 0px;
}
</style>



// WEBPACK FOOTER //
// Transfer.vue?a2d11e72